import { useCallback, useEffect, useMemo, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import { AddApiKeyResult, ApiKeyType } from "@common/models/ApiKey.model";
import useWorkspaceAppsApiConsumer from "@main/consumers/useWorkspaceAppsApiConsumer";
import useWorkspaceDataApiConsumer from "@main/consumers/useWorkspaceDataApiConsumer";
import { setLoadingAction } from "@main/context/App/App.actions";
import { useAppContext, useAppDispatch } from "@main/context/App/App.context";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { useUpdateSelectedWorkspace } from "@main/layouts/views/WorkspaceRootLayout";
import { RoutePaths } from "@main/router/config/routePaths";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { generatePath, useNavigate, useParams } from "react-router-dom";

import { useAdministrationDeps } from "../Administration";
import { determineInitialSelectedAppId } from "../util/determineInitialSelectedApp";

export default function useAppsGeneralViewConsumer() {
  const { administrationService } = useAdministrationDeps();
  const { fetchAllApps, updateAllApps } = useWorkspaceAppsApiConsumer();
  const { fetchWorkspaceDataAndDispatch } = useWorkspaceDataApiConsumer();
  const { appKey } = useParams();
  const { updateSelectedWorkspace } = useUpdateSelectedWorkspace();
  const appDispatch = useAppDispatch();

  const { apps, loading: appsLoading } = useAppContext();
  const { selectedWorkspace, selectedApp: globalSelectedApp } = useAppSelectionContext();

  const selectedWorkspaceId = useMemo(() => selectedWorkspace?.id, [selectedWorkspace]);

  const [localSelectedAppId, setLocalSelectedAppId] = useState<string | undefined>(
    determineInitialSelectedAppId(globalSelectedApp, apps, window.location.search, appKey),
  );

  const localSelectedApp = useMemo(() => apps.find((app) => app.id === localSelectedAppId), [localSelectedAppId, apps]);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const updateApp = useCallback(
    async (name: string) => {
      try {
        if (!selectedWorkspace?.id || name.trim().length === 0 || !localSelectedApp) return;

        setSaving(true);
        await administrationService.updateApp(selectedWorkspace.id, localSelectedApp.id, { name });

        await fetchAllApps(selectedWorkspace.id);

        displayToast({
          title: "Fantastic!",
          content: "App has been updated",
        });
      } catch (error) {
        displayToast({
          title: "Something went wrong",
          content: "Please try again.",
        });
      } finally {
        setSaving(false);
      }
    },
    [localSelectedApp, selectedWorkspace, administrationService, fetchAllApps],
  );

  const updateAppLogo = useCallback(
    async (file: File, toggle: () => void) => {
      try {
        if (!selectedWorkspace?.id || !localSelectedApp || !file) return;

        setSaving(true);
        await administrationService.updateAppLogo(selectedWorkspace.id, localSelectedApp.id, file);

        await fetchAllApps(selectedWorkspace.id);

        displayToast({
          title: "Fantastic!",
          content: "App logo has been uploaded",
        });
      } catch (error) {
        displayToast({
          title: "Something went wrong",
          content: "Please try again.",
        });
      } finally {
        setSaving(false);
        toggle();
      }
    },
    [localSelectedApp, selectedWorkspace, administrationService, fetchAllApps],
  );

  const deleteAppLogo = useCallback(
    async (toggle: () => void) => {
      try {
        if (!selectedWorkspace?.id || !localSelectedApp) return;

        setSaving(true);
        await administrationService.deletAppLogo(selectedWorkspace.id, localSelectedApp.id);

        await fetchAllApps(selectedWorkspace.id);

        displayToast({
          title: "Fantastic!",
          content: "App logo has been removed",
        });
      } catch (error) {
        displayToast({
          title: "Something went wrong",
          content: "Please try again.",
        });
      } finally {
        setSaving(false);
        toggle();
      }
    },
    [localSelectedApp, selectedWorkspace, administrationService, fetchAllApps],
  );

  const activateInactivateApp = async (active: boolean) => {
    try {
      if (!selectedWorkspace?.id || !localSelectedApp) return;

      setSaving(true);

      /// This is tied exclusively to fetchApps request. In this case
      /// we need it "sooner" because of the sidebar links
      appDispatch(setLoadingAction(true));

      !active && (await administrationService.updateApp(selectedWorkspace.id, localSelectedApp.id, { active: true }));
      active && (await administrationService.deactivateApp(selectedWorkspace.id, localSelectedApp.id));

      const isActiveFinal = !active;

      // TODO - if final state is active. Socket will fire off and cause a duplicate request. No big deal for now.
      // Keeping it to ensure correct state even if sockets don't connect.
      await updateSelectedWorkspace(selectedWorkspace); /// Triggers app fetch

      const content = isActiveFinal ? "has been activated" : "has been archived";
      const contentShort = isActiveFinal ? "activated" : "archived";

      displayToast({
        title: `App ${contentShort}`,
        content: `${localSelectedApp.name} ${localSelectedApp.platform.name} ${content}`,
      });
    } catch (error) {
      /// This is tied exclusively to fetchApps request. In this case
      /// we need it "sooner" because of the sidebar links
      appDispatch(setLoadingAction(false));

      displayToast({
        title: "Something went wrong",
        content: "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const setSheldonEnabled = useCallback(
    async (enabled: boolean) => {
      try {
        if (!selectedWorkspace?.id || !localSelectedApp) return;

        setSaving(true);
        await administrationService.updateApp(selectedWorkspace.id, localSelectedApp.id, { ai_enabled: enabled });

        await fetchAllApps(selectedWorkspace.id);

        displayToast({
          title: "Fantastic!",
          content: "Sheldon has been " + (enabled ? "enabled" : "disabled."),
        });
      } catch (error) {
        displayToast({
          title: "Something went wrong",
          content: "Please try again.",
        });
      } finally {
        setSaving(false);
      }
    },
    [localSelectedApp, selectedWorkspace, administrationService, fetchAllApps],
  );

  const deleteApp = async (appId: string) => {
    setLoading(true);

    try {
      if (!selectedWorkspace?.id) return;

      await administrationService.deleteApp(selectedWorkspace.id, appId);

      await updateAllApps(selectedWorkspace.id);

      navigate(
        generatePath(RoutePaths.ADMINISTRATION_GENERAL, {
          workspaceSlug: selectedWorkspace?.slug,
        }),
      );

      displayToast({
        content: `Your app ${localSelectedApp?.name} has been removed`,
      });
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: error?.response?.data?.message ?? "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  async function getRules() {
    try {
      if (!selectedWorkspace?.id || !localSelectedApp || appsLoading || !localSelectedApp.active) return [];
      const { data } = await administrationService.getRules(selectedWorkspace.id, localSelectedApp.id);

      return data;
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: "There was an error while getting rules",
      });
    }
  }

  const deleteRule = async (ruleId: string) => {
    try {
      if (!selectedWorkspace?.id || !localSelectedApp) return;

      setLoading(true);

      await administrationService.deleteRule(selectedWorkspace.id, localSelectedApp.id, ruleId);

      await refetchRules();

      displayToast({
        content: "Rule has been successfully deleted!",
      });
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: "There was an error while deleting rule",
      });
    } finally {
      setLoading(false);
    }
  };

  const addApiKey = async (type: ApiKeyType, startTrial = false): Promise<AddApiKeyResult> => {
    try {
      if (!selectedWorkspace?.id || !localSelectedApp) return "failed";
      setLoading(true);
      await administrationService.addApiKey(selectedWorkspace.id, localSelectedApp.id, type, startTrial);
      // The trial flips is_free and rest_api_trial_available on the workspace, which live on /me.
      if (startTrial) await fetchWorkspaceDataAndDispatch();
      await fetchAllApps(selectedWorkspace.id);
      return "created";
    } catch (error) {
      // The backend refuses REST API keys with 403 when the plan has no API access and no trial is left.
      if (type === ApiKeyType.API && isAxiosError(error) && error.response?.status === 403) return "plan_limited";
      displayToast({
        title: "Something went wrong",
        content: "There was an error while generating API key",
      });
      return "failed";
    } finally {
      setLoading(false);
    }
  };

  const deleteApiKey = async (apiKeyId: string) => {
    try {
      if (!selectedWorkspace?.id || !localSelectedApp) return;
      setLoading(true);
      await administrationService.deleteApiKey(selectedWorkspace.id, localSelectedApp.id, apiKeyId);
      await fetchAllApps(selectedWorkspace.id);
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: "There was an error while deleting API key",
      });
    } finally {
      setLoading(false);
    }
  };

  const addAppDomain = async (domain: string, verification_email: string) => {
    try {
      if (!selectedWorkspace?.id || !localSelectedApp) return;
      setLoading(true);
      await administrationService.addAppDomain(selectedWorkspace.id, localSelectedApp.id, domain, verification_email);
      await fetchAllApps(selectedWorkspace.id);
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: "There was an error while adding domains",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteAppDomain = async (domainId: string) => {
    try {
      if (!selectedWorkspace?.id || !localSelectedApp) return;
      setLoading(true);
      await administrationService.deleteAppDomain(selectedWorkspace.id, localSelectedApp.id, domainId);
      await fetchAllApps(selectedWorkspace.id);
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: "There was an error while deleting domain",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedWorkspace || localSelectedAppId) return;
    fetchAllApps(selectedWorkspace.id).then((allApps) => {
      setLocalSelectedAppId(determineInitialSelectedAppId(globalSelectedApp, allApps, window.location.search, appKey));
    });
  }, [selectedWorkspace, fetchAllApps, localSelectedAppId, globalSelectedApp, appKey]);

  useEffect(() => {
    if (appKey) {
      setLocalSelectedAppId(determineInitialSelectedAppId(globalSelectedApp, apps, window.location.search, appKey));
    }
  }, [appKey, apps, globalSelectedApp]);

  const { data: rules, refetch: refetchRules } = useQuery(
    [`rules`, { localSelectedAppId, selectedWorkspaceId }],
    getRules,
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled:
        selectedWorkspace &&
        Boolean(selectedWorkspaceId) &&
        localSelectedApp &&
        localSelectedApp.active &&
        !appsLoading,
    },
  );

  return {
    updateApp,
    activateInactivateApp,
    deleteApp,
    allApps: apps,
    saving,
    localSelectedApp,
    updateAppLogo,
    deleteAppLogo,
    appsLoading,
    rules: rules ?? [],
    refetchRules,
    deleteRule,
    addApiKey,
    deleteApiKey,
    addAppDomain,
    deleteAppDomain,
    setSheldonEnabled,
    loading,
  };
}
